// Structured data for the brief pages, shared by the React route and the
// build-time prerender so the two cannot describe the same page differently.
//
// The brief pages carried only a bare WebSite block, while the guides and the
// stay page were marked up properly. For the AI-first goal that is backwards:
// the brief is the daily, dated, citable thing on this site, and an answer
// engine had nothing telling it the date, the author or the subject.

const PUBLISHER = {
  '@type': 'Organization',
  name: 'Charleston.Vacations',
  url: 'https://charleston.vacations/'
};

export function websiteJsonLd(origin) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Charleston.Vacations',
    url: `${origin}/`,
    description:
      'A daily conditions and what-to-do brief for visitors to Charleston, SC. Updated every morning.'
  };
}

/**
 * NewsArticle for one day's brief.
 *
 * NewsArticle rather than Article or BlogPosting: this is a dated report on
 * conditions in one place, superseded the next morning, which is what the
 * type is for and what makes the date meaningful to a crawler.
 *
 * @param {object} brief     the brief payload
 * @param {string} pageUrl   absolute url of the page being described
 * @param {string} origin    site origin, for the publisher and image
 */
export function briefArticleJsonLd(brief, pageUrl, origin) {
  if (!brief?.date) return null;

  const headline = brief.score?.headline
    ? `Charleston conditions, ${brief.date}: ${brief.score.headline}`
    : `Charleston conditions for ${brief.date}`;

  // datePublished has to be a real instant. generatedAt is one; the date alone
  // is a calendar day, so it is anchored at the hour the brief actually ships.
  const published = brief.generatedAt || `${brief.date}T06:00:00-04:00`;

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    // Schema.org asks for 110 characters or fewer on headline.
    headline: headline.length > 110 ? `${headline.slice(0, 107)}...` : headline,
    description: brief.weatherSummary || brief.score?.headline || undefined,
    datePublished: published,
    dateModified: published,
    // The brief is written by the site, not by a person; naming a fake byline
    // would be worse than naming the publisher twice.
    author: PUBLISHER,
    publisher: { ...PUBLISHER, url: `${origin}/` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    isAccessibleForFree: true,
    inLanguage: 'en-US',
    about: {
      '@type': 'Place',
      name: 'Charleston, South Carolina',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Charleston',
        addressRegion: 'SC',
        addressCountry: 'US'
      }
    }
  };
}
